<script>
/**
 * Sparkline do card de insight: a linha da série e o degradê que sai dela.
 *
 * SVG próprio, e não o `v-sparkline` do Vuetify, porque aquele faz um OU outro
 * — com `fill` ele não traça a linha — e o protótipo tem os dois.
 *
 * Vive num arquivo separado do card por ser a única parte dele com preocupação
 * própria (id de gradiente único, viewBox, tokens de traço). Fora daqui o card
 * passava de 540 linhas.
 */
import { caminhoDaLinha, caminhoDaArea } from './sparkline'

// Fixo porque a lateral tem largura fixa — não há cenário responsivo aqui.
// ⚠️ O valor é a geometria do `viewBox` E o tamanho renderizado: com
// `preserveAspectRatio="none"` uma caixa CSS diferente do viewBox esticaria o
// traço num eixo só. Mudou aqui, mude o `width` do `.ia-sparkline` junto.
// 112 (era 84) desde que a lateral da imersiva passou a 400px: o card `serie` é
// o único do bloco que reparte a linha entre número e gráfico, e o gráfico
// encolhido ao lado de um valor de 17px lia como enfeite.
const LARGURA = 112
const ALTURA = 30

// Onda do FANTASMA (estado de carregando). Traçada em cinza, pela MESMA função
// de geometria do gráfico real — é o que garante que a silhueta pendente e a
// carregada tenham a mesma cara. Não afirma tendência: o cinza diz "gráfico
// vindo", do mesmo jeito que a barra cinza diz "número vindo".
const ONDA_FANTASMA = [14, 20, 16, 26, 22, 30, 27, 34]

export default {
  name: 'IaSparkline',
  props: {
    serie: { type: Array, default: () => [] },
    // Carregando: mesma curva desenhada em cinza, com o pulso do esqueleto.
    fantasma: { type: Boolean, default: false },
  },
  data() {
    return { LARGURA, ALTURA }
  },
  computed: {
    // ⚠️ Id de gradiente é GLOBAL no documento: dois sparklines na mesma tela
    // com o mesmo id fariam o segundo referenciar o degradê do primeiro.
    // `_uid` é único por instância Vue.
    idDoGradiente() {
      return 'ia-spark-' + this._uid
    },
    pontos() {
      return this.fantasma ? ONDA_FANTASMA : this.serie
    },
    caminhoLinha() {
      return caminhoDaLinha(this.pontos, LARGURA, ALTURA)
    },
    caminhoArea() {
      return caminhoDaArea(this.pontos, LARGURA, ALTURA)
    },
  },
}
</script>

<template>
  <!-- `preserveAspectRatio="none"` deixa o viewBox casar exatamente com a
       caixa de 84×30. -->
  <svg
    v-if="caminhoLinha"
    class="ia-sparkline"
    :class="{ 'ia-sparkline--fantasma': fantasma }"
    :viewBox="`0 0 ${LARGURA} ${ALTURA}`"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <defs>
      <linearGradient :id="idDoGradiente" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" class="ia-sparkline__topo" />
        <stop offset="100%" class="ia-sparkline__base" />
      </linearGradient>
    </defs>
    <path :d="caminhoArea" :fill="`url(#${idDoGradiente})`" />
    <path
      :d="caminhoLinha"
      fill="none"
      class="ia-sparkline__linha"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      vector-effect="non-scaling-stroke"
    />
    <!-- ⚠️ `vector-effect="non-scaling-stroke"` (06/09, achado do usuário —
         "o gráfico tá ficando mais grosso"): sem ele, `preserveAspectRatio=
         "none"` estica o `stroke-width` JUNTO com o resto — um consumidor
         que force a caixa CSS maior que o viewBox (ex: `.ia-insight__serie-
         grafico` do `serie`, 48px contra o viewBox de 30) engrossa a linha
         na mesma proporção. Com o efeito, a espessura fica fixa em pixels de
         TELA, e a linha não muda de grossura seja qual for o tamanho da
         caixa. -->
  </svg>
</template>

<style lang="sass" scoped>
// ★ Largura CHEIA do CONTAINER (não do card). Os 112px fixos existiam quando
// o sparkline era irmão do texto numa fileira — travá-lo impedia que
// engolisse a coluna e quebrasse o valor em três linhas.
//
// ⚠️ 06/09 — voltou a ser irmão de texto numa linha, desta vez só no `serie`
// (`ia-insight-card.vue::.ia-insight__serie-rodape`, pedido do usuário com
// print de referência): "gráfico só na metade". A trava não voltou aqui —
// o CALLER limita o container a 50% (`.ia-insight__serie-grafico`), e este
// `width: 100%` continua servindo os dois casos (metade do card, ou o card
// inteiro) sem o componente precisar saber qual é qual.
//
// O `viewBox` de 112 do script continua valendo — com `preserveAspectRatio`
// em `none` ele estica, e é exatamente para isso que o atributo está lá.
.ia-sparkline
  width: 100%
  height: 30px

// Cor por CSS, não por atributo: assim o traço e as paradas do degradê usam os
// mesmos tokens do resto do card, sem hex no template.
//
// ★ 65% do verde, não o verde cheio (28/08). Em `--success` sólido a curva era
// o elemento mais forte do card e puxava o olho antes do número, que é o
// assunto. Diluído, ela vira o que deve ser: a forma da tendência, lida de
// relance. `color-mix` mantém tudo derivado do token, sem rgba literal.
//
// ⚠️ A espessura fica em 2 (atributo no template). Afinar junto some com a
// curva em tela retina, e a leitura de "subiu ou caiu" depende dela.
.ia-sparkline__linha
  stroke: color-mix(in srgb, var(--success) 65%, transparent)

// ★ 16% (era 30). A área é o eco da linha — precisa ficar um degrau ABAIXO
// dela, senão as duas competem e o card ganha um bloco verde que ninguém pediu.
// Com a linha também mais fraca, manter os 30 invertia a hierarquia entre as
// duas. `--success-bg-soft` (15%) chega perto, mas some sobre a superfície
// quente da lateral — por isso o `color-mix` e não o token pronto.
.ia-sparkline__topo
  stop-color: color-mix(in srgb, var(--success) 16%, transparent)

.ia-sparkline__base
  stop-color: transparent

// Fantasma: mesma curva, sem cor e sem afirmação. `ia-shimmer` é a keyframe
// GLOBAL de `design/_ia-shimmer.sass` — pulso de opacidade, que compõe na GPU.
.ia-sparkline--fantasma
  animation: ia-shimmer 1.4s ease-in-out infinite

  .ia-sparkline__linha
    stroke: var(--neutral-300)

  .ia-sparkline__topo
    stop-color: color-mix(in srgb, var(--neutral-400) 35%, transparent)

@media (prefers-reduced-motion: reduce)
  .ia-sparkline--fantasma
    animation: none
</style>
